import React, { useRef, useState } from 'react';

import { KeyboardAvoidingView } from '@rbilabs/universal-components';
import { KeyboardController } from 'react-native-keyboard-controller';

import { BasicInput } from 'components/basic-input';
import { t } from 'state/intl/translations';

import { TransactionInputFormContainer } from '../loyalty-claim-points.styled';

import { nextTransactionInputValues } from './apply-transaction-input-change';

interface TransactionInputFormProps {
  setCodeValue: (val: string) => void;
  onSubmitEditing?: () => void;
}

const TransactionInputForm = ({
  onSubmitEditing = () => {
    void KeyboardController.dismiss();
  },
  setCodeValue,
}: TransactionInputFormProps) => {
  const inputsRef = useRef<{ [key: string]: any }>({});
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const fields = [
    {
      name: 'first',
      maxLength: 5,
      focusNext: () => inputsRef.current.second?.focus(),
      onSubmitEditing: () => inputsRef.current.second?.focus(),
      returnKeyType: 'next' as const,
    },
    {
      name: 'second',
      maxLength: 5,
      focusNext: () => inputsRef.current.third?.focus(),
      focusBack: () => inputsRef.current.first?.focus(),
      onSubmitEditing: () => inputsRef.current.third?.focus(),
      returnKeyType: 'next' as const,
    },
    {
      name: 'third',
      maxLength: 5,
      focusNext: () => inputsRef.current.fourth?.focus(),
      focusBack: () => inputsRef.current.second?.focus(),
      onSubmitEditing: () => inputsRef.current.fourth?.focus(),
      returnKeyType: 'next' as const,
    },
    {
      name: 'fourth',
      maxLength: 6,
      focusBack: () => inputsRef.current.third?.focus(),
      onSubmitEditing,
      returnKeyType: 'done' as const,
    },
  ];

  const commitValues = (nextValues: Record<string, string>) => {
    setFieldValues(nextValues);
    setCodeValue(fields.map(({ name }) => nextValues[name] ?? '').join(''));
  };

  const applyInput = (inputName: string, nextValue: string, source: 'paste' | 'change') => {
    const nextValues = nextTransactionInputValues({
      fields,
      current: fieldValues,
      inputName,
      nextValue,
      source,
    });

    if (!nextValues) {
      return;
    }

    commitValues(nextValues);

    const previousValue = fieldValues[inputName] ?? '';
    const filledInOrder = source === 'paste' || nextValue.length - previousValue.length > 1;

    if (!filledInOrder) {
      return;
    }

    const firstIncomplete = fields.find(
      field => (nextValues[field.name] ?? '').length < field.maxLength
    );
    const focusTarget = firstIncomplete ?? fields[fields.length - 1];
    inputsRef.current[focusTarget.name]?.focus();
  };

  return (
    <KeyboardAvoidingView>
      <TransactionInputFormContainer>
        {fields.map(({ name, ...field }, index) => (
          <BasicInput
            maxWidth={'86px'}
            flex={1}
            {...field}
            key={name}
            name={name}
            type="number"
            keyboardType="numeric"
            value={fieldValues[name] ?? ''}
            accessibilityLabel={t('claimPointsTransactionIdField', {
              current: index + 1,
              total: fields.length,
              defaultValue: 'Transaction ID, part {current} of {total}',
            })}
            onFieldChange={(inputName, inputText) => applyInput(inputName, inputText, 'change')}
            onPasteText={(inputName, pastedText) => applyInput(inputName, pastedText, 'paste')}
            setRef={(instance: any) => (inputsRef.current[name] = instance)}
          />
        ))}
      </TransactionInputFormContainer>
    </KeyboardAvoidingView>
  );
};

export default TransactionInputForm;
